import type { ErrorEvent } from "@sentry/react"
import { AxiosRequestConfig, isAxiosError } from "axios"
import { API_BASE_URL } from "configs/AppConfig"

import { describeHttpEndpoint, EndpointPathRule, HttpEndpoint } from "@shared/utils/httpEndpoint"

/** Whether a request went to our own API or to an external party. */
export type HttpTarget = "own_api" | "external"

/**
 * Public endpoints take practice, page and care group slugs instead of ids; collapsing them keeps one issue per
 * endpoint instead of one per practice. The postal code lookup's house number is patient data, so it goes too.
 */
const ownApiPathRules: EndpointPathRule[] = [
  [/(\/public\/practices\/)[^/]+/, "$1:slug"],
  [/(\/public\/practices\/:slug\/pages\/)[^/]+/, "$1:pageSlug"],
  [/(\/postalcode\/)[^/]+\/[^/]+/, "$1:postalCode/:houseNumber"],
  [/(\/public\/care-groups\/slugs\/)[^/]+/, "$1:slug"],
]

/** Describes where an axios request went, see `describeHttpEndpoint`. */
export function describeAxiosRequest(config: AxiosRequestConfig | undefined): HttpEndpoint & { target: HttpTarget } {
  const origin = window.location.origin
  const endpoint = describeHttpEndpoint(
    { url: config?.url, baseURL: config?.baseURL, method: config?.method },
    { origin, rules: ownApiPathRules }
  )
  const ownApiHost = new URL(API_BASE_URL, origin).host

  return { ...endpoint, target: endpoint.host === ownApiHost ? "own_api" : "external" }
}

/**
 * Tags an event caused by an axios error with the request's host, target and endpoint, and appends the endpoint
 * to the exception value so it shows in the issue title — e.g. `AxiosError: Network Error (GET /api/v1/settings)`.
 * Like the backend, it splits the default grouping per endpoint; a fingerprint set at the capture site (such as
 * `onResponseRejected`'s) is kept. Other events are returned unchanged.
 */
export function addAxiosRequestContext(event: ErrorEvent, error: unknown): ErrorEvent {
  if (!isAxiosError(error)) {
    return event
  }

  const { method, host, path, target } = describeAxiosRequest(error.config)
  const endpoint = `${method} ${target === "own_api" ? path : `${host}${path}`}`

  event.tags = { ...event.tags, "http.host": host, "http.target": target, "http.endpoint": endpoint }
  event.fingerprint ??= ["{{ default }}", method, host, path]

  const exception = event.exception?.values?.at(-1)
  if (exception?.value) {
    exception.value = `${exception.value} (${endpoint})`
  }

  return event
}
